import css from 'styled-jsx/css';

import colours from './colours';

export const policiesCSS = css`
  div {
    color: ${colours.snBlack};
    padding: 20px;
    margin: auto;
    max-width: 800px;
  }

  h1 {
    font-size: 38px;
    line-height: 1.3;
  }

  h2 {
    font-size: 26px;
    font-weight: 400;
    margin-top: 30px;
    margin-bottom: 0;
  }

  p {
    line-height: 1.6;
    margin-top: 30px;
    margin-bottom: 0;
    font-size: 16px;
  }

  b {
    font-weight: 700;
  }

  a {
    color: ${colours.snBlack};
    font-weight: 900;
    text-decoration: underline;
  }

  ul {
    margin-top: 20px;
  }

  li {
    line-height: 1.6;
    font-size: 17px;
    margin-bottom: 14px;
  }
`;

/**
 * This defines the styling for article content loaded from the Wordpress CMS.
 *
 * It's a global style because we are setting inner HTML for article content.
 *
 * Article Content should be wrapped in a div with the class "article-content". This variable should
 * also be imported into the page where the article content is being rendered - using:
 *
 * ```ts
 *   <style jsx>{articleCSS}</style>
 * ```
 */
export const articleCSS = css.global`
  /* Using .article-content class to avoid (global) clashing with other components on this page */
  .article-content strong {
    font-weight: 500;
  }

  .article-content p,
  .article-content ul,
  .article-content ol,
  .article-content blockquote,
  .article-content table {
    clear: both;
  }

  .article-content p {
    margin-block-start: 1em;
    margin-block-end: 1em;
  }

  .article-content ul,
  .article-content ol {
    display: block;
    margin-block-start: 1em;
    margin-block-end: 1em;
    padding-inline-start: 40px;
  }

  .article-content ul {
    list-style-type: disc;
  }

  .article-content ol {
    list-style-type: decimal;
  }

  .article-content h1,
  .article-content h2,
  .article-content h3 {
    font-weight: 500;
    clear: both;
    margin: 30px 0 10px;
    padding: 0;
  }

  .article-content h1,
  .article-content h2,
  .article-content h3 {
    font-size: 22px;
    line-height: 28px;
  }

  @media (min-width: 768px) {
    .article-content h1,
    .article-content h2,
    .article-content h3 {
      margin: 50px 0 20px;
    }

    .article-content h1 {
      font-size: 36px;
      line-height: 46px;
    }

    .article-content h2,
    .article-content h3 {
      font-size: 26px;
      line-height: 36px;
    }
  }

  /*
    For anchor sections we want to extend the margin upwards so that the <h2> title isn't covered by
    the floating top nav bar when scrolling to the anchor link.
    Also note that we need to subtract a bit from the padding to account for the top margin of h2 (so that the <div> stays in the same place as without this fix).
  */
  .article-content .anchor {
    margin-top: -70px;
    padding-top: 70px;
  }

  .article-content a {
    font-weight: 500;
    text-decoration: underline;
  }

  .article-content a:hover,
  .article-content a:focus,
  .article-content a:active {
    text-decoration: none;
  }

  .article-content blockquote {
    background-color: ${colours.veryLightGray};
    padding: 20px;
    margin: 30px;
    border-left: 6px solid ${colours.lightGray};
  }

  @media (max-width: 767px) {
    .article-content blockquote {
      margin: 20px 0;
      padding: 10px 20px;
    }
  }

  .article-content table {
    display: block;
    margin-top: 50px;
    margin-bottom: 20px;
    width: 100%;
    overflow: auto;
  }

  .article-content table,
  .article-content td {
    border-collapse: collapse;
  }

  .article-content td {
    border: 1px solid ${colours.lightGray};
  }

  .article-content td {
    padding: 15px;
  }

  .article-content td .smaller {
    font-size: 17px;
    line-height: 25px;
  }

  .article-content td .smaller.fineprint {
    font-size: 16px;
    line-height: 23px;
  }

  @media (max-width: 991px) {
    .article-content td {
      padding: 10px;
      font-size: 17px;
      line-height: 25px;
    }

    .article-content td .smaller {
      font-size: 14px;
      line-height: 22px;
    }

    .article-content td .smaller.fineprint {
      font-size: 13px;
      line-height: 19px;
    }
  }

  @media (max-width: 767px) {
    .article-content td {
      padding: 5px;
      font-size: 14px;
      line-height: 22px;
    }

    .article-content td strong {
      font-weight: 900;
    }

    .article-content td .smaller {
      font-size: 11px;
      line-height: 15px;
    }

    .article-content td .smaller.fineprint {
      font-size: 10px;
      line-height: 14px;
    }
  }

  .article-content td p {
    margin-bottom: 0;
  }

  /* Style all article images the same as .img-responsive (so that class names don't need altering) */
  .article-content img {
    display: block;
    max-width: 100%;
    height: auto;
    margin-bottom: 20px;
  }

  /* nav ("Jump to" links) */
  .article-content nav,
  .article-content nav a {
    margin-top: 50px;
    margin-bottom: 50px;
  }

  .article-content nav {
    color: ${colours.blue};
  }

  .article-content pre {
    white-space: pre;
    width: auto;
    max-height: 600px;
    font-size: 13px;
    line-height: 1.30769231;
    color: ${colours.blue};
    background-color: ${colours.marbleWhite};
    border-radius: 5px;
    margin: 0;
    padding: 12px;
    overflow: auto;
    word-wrap: normal;
  }

  .article-content code {
    color: ${colours.blue};
    background-color: ${colours.marbleWhite};
    padding: 3px 5px;
    margin: 0 3px;
    font-size: 14px;
  }

  .article-content pre code {
    white-space: pre-wrap;
  }

  .article-content .cta-button a {
    text-decoration: none;
    position: relative;
    background: transparent;
    -webkit-font-smoothing: antialiased;
    display: inline-block;
    text-align: center;
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
    background-image: none;
    border: 1px solid transparent;
    transition: all 0.15s ease;
    border: none;
    background-color: ${colours.blue};
    border-radius: 2px;
    color: ${colours.white};
    font-weight: 900;
    padding: 14px 28px;
    font-size: 16px;
    line-height: 1;
    height: 44px;
    margin-bottom: 28px;
  }

  .article-content .cta-button a:hover {
    transform: translateY(-1px);
    text-decoration: none;
    outline: 0;
  }

  .article-content .cta-button a:focus {
    outline: none;
    text-decoration: none;
    outline-offset: -2px;
  }

  .article-content .cta-button a:active {
    transform: translateY(1px);
  }
`;
